'use client'
import { cn, Modal, ModalContent, ModalProps } from "@heroui/react"
import { useTranslations } from "next-intl"
import { ReactNode } from "react"
import { Luxury } from "@/ui/icons/Luxury"
import { Synergy } from "@/ui/icons/Synergy"
import { PerkStar } from "@/ui/icons/PerkStar"
import { X } from "@/ui/icons/X"
import { mergeModalLayerClassNames, useModalLayer } from "@/ui/modalLayer"
import { useUserDetailStore } from "@/store/useUserDetailStore"

export type PerkPromptModalProps = Omit<ModalProps, "children"> & {
  title: ReactNode
  subTitle?: ReactNode
  header?: ReactNode
  perks: ReactNode
  perkClassName?: string
  children?: ReactNode | ((onClose: () => void) => ReactNode)
}

export const PerkItem = ({
  icon: Icon,
  children,
}: {
  icon: React.ComponentType<any>
  children: ReactNode
}) => (
  <li className="flex flex-row items-center gap-2 text-center lg:items-center  lg:text-left">
    <Icon width={24} height={24} className="size-6 shrink-0" />
    <span className="text-[12px] leading-4 tracking-[-0.3px] text-[#706D90]">
      {children}
    </span>
  </li>
)

export const PerkPromptModal = ({
  title,
  subTitle,
  header,
  perks,
  perkClassName,
  children,
  isOpen,
  classNames,
  ...props
}: PerkPromptModalProps) => {
  const t = useTranslations()
  const hasHeader = header != null
  const layer = useModalLayer(isOpen)
  const isLuxuryMember = useUserDetailStore((state) => Number(state.vip_type) === 1)
  const BadgeIcon = isLuxuryMember ? Synergy : Luxury

  return (
    <Modal
      hideCloseButton
      placement="center"
      isOpen={isOpen}
      classNames={mergeModalLayerClassNames({
        ...classNames,
        wrapper: cn("overflow-visible", classNames?.wrapper),
        base: cn(
          "bg-transparent shadow-none overflow-visible",
          hasHeader
            ? "w-[334px] max-w-[334px] lg:w-[424px] lg:max-w-[424px]"
            : "w-[334px] max-w-[334px] lg:w-fit lg:max-w-none",
          classNames?.base,
        ),
        backdrop: cn("bg-black/50", classNames?.backdrop),
      }, layer)}
      {...props}
    >
      <ModalContent className="overflow-visible bg-transparent shadow-none">
        {(onClose) => (
          <div className="flex flex-col items-center lg:min-w-[424px]">
            <div className="w-full">
              {hasHeader && (
                <div className="rounded-t-[24px] bg-[#E3E1F2] px-5 pb-8 pt-3">
                  {header}
                </div>
              )}
              <div className={cn(
                "w-full rounded-[24px] bg-[#F7F8FF] px-3 pb-5 pt-6",
                hasHeader && "-mt-6",
              )}>
                <h2 className="text-center text-[18px] font-bold leading-[22px] tracking-[-0.3px] text-[#332E60]">
                  {title}
                </h2>
                {subTitle != null && (
                  <p className="mt-3 px-1 text-[14px] font-medium leading-[18px] tracking-[-0.3px] text-[#706D90] max-w-lg">
                    {subTitle}
                  </p>
                )}

                <div className="mt-4 flex flex-col rounded-[20px] border-y-2 border-[#E4DCF9] bg-white px-3 pb-5 pt-4">
                  <div className="relative flex h-6 items-center justify-center">
                    <div className="pointer-events-none absolute inset-x-8 h-6 bg-gradient-to-r from-transparent via-[#6440F9]/20 to-transparent" />
                    <PerkStar width={8} height={8} className="absolute left-5 size-2" />
                    <PerkStar width={12} height={12} className="absolute left-8 size-3" />
                    <PerkStar width={12} height={12} className="absolute right-8 size-3 -scale-y-100 rotate-180" />
                    <PerkStar width={8} height={8} className="absolute right-5 size-2 -scale-y-100 rotate-180" />
                    <span className="relative z-10 flex items-center gap-1 text-[12px] font-semibold leading-4 tracking-[-0.3px] text-[#5C3D85]">
                      <BadgeIcon width={16} height={16} className="size-4" />
                      {t("pricing.unlockPerks")}
                    </span>
                  </div>

                  <ul className={cn(
                    "mt-5 grid gap-4",
                    perkClassName ?? (hasHeader ? "grid-cols-2" : "grid-cols-1 lg:grid-cols-2"),
                  )}>
                    {perks}
                  </ul>

                  {typeof children === "function" ? children(onClose) : children}
                </div>
              </div>
            </div>

            <button
              type="button"
              aria-label={t("common.cancel")}
              className="mt-8 flex size-8 items-center justify-center rounded-full border-2 border-white/70"
              onClick={onClose}
            >
              <X width={16} height={16} className="size-4 text-white" />
            </button>
          </div>
        )}
      </ModalContent>
    </Modal>
  )
}
